import BaseController from "./base"

export default class extends BaseController {
  static targets = ["form", "nameInput", "appleIdInput", "newAppleCheckbox", "spotifyUriInput", "newSpotifyCheckbox", "errors", "errorList", "submitButton", "submitText", "submitSpinner", "contributorFlag"]

  connect() {
    super.connect?.()
    
    // Focus on name input when modal opens
    if (this.hasNameInputTarget) {
      this.nameInputTarget.focus()
    }
  }

  async submit(event) {
    event.preventDefault()
    
    // Validate form before submitting
    const validationErrors = this.validateForm()
    if (validationErrors.length > 0) {
      this.showErrors(validationErrors)
      return
    }
    
    const formData = new FormData(this.formTarget)
    
    // Show loading state
    this.setSubmitButtonLoading(true)
    this.hideErrors()
    
    const { data, success } = await this.submitForm(formData)
    
    if (success) {
      // Success - close modal and handle success
      this.closeModal()
      this.handleSubmitSuccess(data.artist)
      this.resetForm()
    } else {
      // Show validation errors
      this.showErrors(data.errors || ['An error occurred'])
    }
    
    this.setSubmitButtonLoading(false)
  }

  validateForm() {
    const errors = []

    // Check artist name
    const nameError = this.validateRequired(this.nameInputTarget.value, "Artist name")
    if (nameError) errors.push(nameError)

    // Check if this is a contributor - if so, skip Apple ID and Spotify URI validation
    const isContributor = this.hasContributorFlagTarget && this.contributorFlagTarget.value === "true"

    if (!isContributor) {
      // Check Apple validation - support both old checkbox and new radio button approach
      const isNewAppleArtist = this.hasNewAppleCheckboxTarget &&
        (this.newAppleCheckboxTarget.checked || this.newAppleCheckboxTarget.value === '1')

      // Get Apple ID from either the visible input or hidden field
      let hasAppleId = false
      if (this.hasAppleIdInputTarget && this.appleIdInputTarget.value.trim()) {
        hasAppleId = true
      }
      // Also check hidden field for search selection (works for both add and edit forms)
      const appleHiddenField = this.element.querySelector('[name="artist[apple_id]"]')
      if (appleHiddenField && appleHiddenField.value.trim()) {
        hasAppleId = true
      }

      if (!hasAppleId && !isNewAppleArtist) {
        errors.push("Please select an Apple artist, provide an Apple ID, or choose 'New Apple Artist'")
      }

      // Check Spotify validation - support both old checkbox and new radio button approach
      const isNewSpotifyArtist = this.hasNewSpotifyCheckboxTarget &&
        (this.newSpotifyCheckboxTarget.checked || this.newSpotifyCheckboxTarget.value === '1')

      // Get Spotify URI from either the visible input or hidden field
      let hasSpotifyUri = false
      if (this.hasSpotifyUriInputTarget && this.spotifyUriInputTarget.value.trim()) {
        hasSpotifyUri = true
      }
      // Also check hidden field for search selection (works for both add and edit forms)
      const spotifyHiddenField = this.element.querySelector('[name="artist[spotify_uri]"]')
      if (spotifyHiddenField && spotifyHiddenField.value.trim()) {
        hasSpotifyUri = true
      }

      if (!hasSpotifyUri && !isNewSpotifyArtist) {
        errors.push("Please select a Spotify artist, provide a Spotify ID, or choose 'New Spotify Artist'")
      }
    }

    // Validate Apple ID format if provided via manual input (regardless of contributor status)
    if (this.hasAppleIdInputTarget) {
      const appleId = this.appleIdInputTarget.value.trim()
      if (appleId && !/^\d+$/.test(appleId)) {
        errors.push("Apple ID must contain only digits")
      }
    }

    // Validate Spotify URI format if provided via manual input
    if (this.hasSpotifyUriInputTarget) {
      const spotifyUri = this.spotifyUriInputTarget.value.trim()
      if (spotifyUri) {
        // Allow just ID or full URI format
        const isValidFormat = /^spotify:artist:[a-zA-Z0-9]+$/.test(spotifyUri) ||
                             /^[a-zA-Z0-9]+$/.test(spotifyUri)
        if (!isValidFormat) {
          errors.push("Spotify ID must be alphanumeric or in format spotify:artist:XXXXX")
        }
      }
    }

    return errors
  }

  // Abstract methods to be implemented by subclasses
  async submitForm(formData) {
    throw new Error("submitForm() must be implemented by subclass")
  }

  handleSubmitSuccess(artist) {
    throw new Error("handleSubmitSuccess() must be implemented by subclass")
  }

  // Common form reset with specific focus
  resetForm() {
    super.resetForm()
    
    if (this.hasNameInputTarget) {
      this.nameInputTarget.focus()
    }
  }
}